/**
 * PATH: apps/quote/src/pages/import-quote/importExistingQuotes.ts
 * FILE_ID: QUOTE-IMPORT-EXISTING-COPY-001
 * PURPOSE: Copy selected Quotech quotes into new builder quotes without AI parse.
 */
import { quotesCrudApi } from '@/lib/api/quotes-crud-api'
import type { JobQuoteEntry, QuoteVersionRow } from '@/lib/quote-api-types'
import {
  fromJobEntry,
  fromVersionRow,
  quoteFamilyKey,
} from './existingQuoteFamilies'
import type { ImportableQuote } from './existingQuoteTypes'

type ExistingQuoteApi = {
  duplicateQuote: (
    id: string,
    asRevision?: boolean,
    jobId?: number | string | null,
  ) => Promise<{ id: string }>
  createBuilderQuoteFromSheet: (
    jobId: number | string,
    sheetId: number,
  ) => Promise<{ builder_quote_id: string }>
  listQuoteVersions?: (id: string) => Promise<QuoteVersionRow[]>
  getJobQuotes?: (jobId: number | string) => Promise<{
    groups?: Array<{ key: string; entries: JobQuoteEntry[] }>
  }>
}

export async function importExistingQuotes(
  members: ImportableQuote[],
  api: ExistingQuoteApi = quotesCrudApi,
  /** The job being imported onto. Omitted keeps the historical behaviour. */
  targetJobId?: number | string | null,
): Promise<string[]> {
  const ids: string[] = []
  for (const member of members) {
    if (member.builderQuoteId) {
      const dup = await api.duplicateQuote(member.builderQuoteId, false, targetJobId)
      if (dup?.id) ids.push(dup.id)
      continue
    }
    if (member.sheetId != null && member.jobId != null) {
      const minted = await api.createBuilderQuoteFromSheet(member.jobId, member.sheetId)
      if (minted?.builder_quote_id) ids.push(minted.builder_quote_id)
    }
  }
  return ids
}

export async function expandFamilyMembers(
  seed: ImportableQuote,
  api: ExistingQuoteApi = quotesCrudApi,
): Promise<ImportableQuote[]> {
  const extras: ImportableQuote[] = []
  const seedStem = quoteFamilyKey(seed.quoteNumber)

  if (seed.builderQuoteId && api.listQuoteVersions) {
    try {
      const versions = await api.listQuoteVersions(seed.builderQuoteId)
      for (const version of versions) {
        const row = fromVersionRow(version, seed.jobId)
        if (row) extras.push(row)
      }
    } catch {
      // List/job rows still import; versions are additive.
    }
  }

  if (seed.jobId && api.getJobQuotes) {
    try {
      const job = await api.getJobQuotes(seed.jobId)
      for (const group of job.groups || []) {
        if (group.key === 'other') continue
        for (const entry of group.entries || []) {
          const stem = quoteFamilyKey(entry.quote_number)
          if (stem === seedStem || entry.is_variation || entry.is_contract) {
            extras.push(fromJobEntry(entry, seed.jobId))
          }
        }
      }
    } catch {
      // Same: family still works from the list row.
    }
  }

  return extras
}
